import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Check, X, Layers } from 'lucide-react';
import { getCategories, getModels, addCategory, updateCategory, deleteCategory } from '../../services/adminStorage';

export default function CategoriesManager() {
  const [categories, setCategories] = useState(getCategories());
  const [models, setModels] = useState(getModels());
  
  const [newCatLabel, setNewCatLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');

  useEffect(() => {
    const handleUpdate = () => {
      setCategories(getCategories());
      setModels(getModels());
    };
    window.addEventListener('suzume_data_updated', handleUpdate);
    return () => window.removeEventListener('suzume_data_updated', handleUpdate);
  }, []);

  // Conta quantos modelos existem em cada categoria
  const getCategoryCount = (slug) => {
    if (slug === 'todos') return models.length;
    return models.filter(m => m.categorySlug === slug).length;
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCatLabel.trim()) return;

    const slug = newCatLabel.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const updated = await addCategory({ id: slug, label: newCatLabel.trim() });
    setCategories(updated);
    setNewCatLabel('');
  };

  const handleStartEdit = (cat) => {
    setEditingId(cat.id);
    setEditingLabel(cat.label);
  };

  const handleSaveEdit = async (id) => {
    if (editingLabel.trim()) {
      const updated = await updateCategory(id, editingLabel.trim());
      setCategories(updated);
    }
    setEditingId(null);
  };

  const handleDelete = async (id, label) => {
    if (id === 'todos') {
      alert('A categoria principal "Todos os Nichos" não pode ser excluída.');
      return;
    }
    if (window.confirm(`Deseja excluir a categoria "${label}"?`)) {
      const updated = await deleteCategory(id);
      setCategories(updated);
    }
  };

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.75rem', color: 'var(--text-title)', fontWeight: 800 }}>
          Gerenciar Nichos &amp; Categorias
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
          Crie e altere as categorias de mercado utilizadas para filtrar os modelos no catálogo.
        </p>
      </div>

      {/* Cadastrar Nova Categoria */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', marginBottom: '2.5rem', boxShadow: 'var(--shadow-sm)' }}>
        <h3 style={{ fontSize: '1.1rem', color: 'var(--text-title)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Plus size={18} color="var(--color-brand-primary)" />
          Adicionar Novo Nicho / Categoria
        </h3>

        <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <input 
              type="text"
              required
              placeholder="Ex: Odontologia Estética, Pet Shop, Barbearias..."
              value={newCatLabel}
              onChange={(e) => setNewCatLabel(e.target.value)}
              className="form-control"
            />
          </div>
          <button type="submit" className="btn btn-primary">
            <Plus size={18} /> Cadastrar Categoria
          </button>
        </form>
      </div>

      {/* Lista de Categorias */}
      <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
        <h3 style={{ fontSize: '1.15rem', color: 'var(--text-title)', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Layers size={18} color="var(--color-brand-primary)" />
          Categorias Ativas no Sistema
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {categories.map((cat) => {
            const isEditing = editingId === cat.id;
            const count = getCategoryCount(cat.id);

            return (
              <div 
                key={cat.id}
                style={{
                  background: 'var(--bg-main)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                {isEditing ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                    <input 
                      type="text"
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      className="form-control"
                      style={{ padding: '0.35rem 0.65rem', fontSize: '0.85rem' }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={() => handleSaveEdit(cat.id)}>
                      <Check size={14} />
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--text-title)', fontSize: '0.95rem' }}>
                        {cat.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {count} {count === 1 ? 'modelo' : 'modelos'} cadastrados
                      </div>
                    </div>

                    {cat.id !== 'todos' && (
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button 
                          className="btn btn-secondary btn-sm" 
                          onClick={() => handleStartEdit(cat)}
                          title="Editar Nome"
                          style={{ padding: '0.35rem 0.5rem' }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button 
                          className="btn btn-sm" 
                          onClick={() => handleDelete(cat.id, cat.label)}
                          style={{ padding: '0.35rem 0.5rem', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}
                          title="Excluir Categoria"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
